import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { Portal } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useShallow } from 'zustand/react/shallow';

import { useTranslation } from '@/i18n';
import { useGlobalAlertStore } from '@/stores/useGlobalAlertStore';
import { fontSize, iconSize, radii, spacing, unisonColors } from '@/theme';

const DURATION_SUCCESS = 3000;
const DURATION_ERROR = 5000;
const ANIMATION_DURATION = 200;

const variantStyles = {
  success: {
    backgroundColor: unisonColors.success,
    shadowColor: '#007350',
  },
  error: {
    backgroundColor: unisonColors.error,
    shadowColor: unisonColors.onErrorContainer,
  },
} as const;

export const SnackbarProvider = () => {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { currentAlert, clearGlobalAlert } = useGlobalAlertStore(
    useShallow((state) => ({
      currentAlert: state.alerts[0] ?? null,
      clearGlobalAlert: state.clearGlobalAlert,
    })),
  );

  const opacity = useMemo(() => new Animated.Value(0), []);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    Animated.timing(opacity, {
      toValue: 0,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) clearGlobalAlert();
    });
  }, [clearGlobalAlert, opacity]);

  useEffect(() => {
    if (!currentAlert) return;

    opacity.setValue(0);
    Animated.timing(opacity, {
      toValue: 1,
      duration: ANIMATION_DURATION,
      useNativeDriver: true,
    }).start();

    const duration = currentAlert.variant === 'error' ? DURATION_ERROR : DURATION_SUCCESS;
    timerRef.current = setTimeout(dismiss, duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentAlert, dismiss, opacity]);

  if (!currentAlert) return null;

  const colors = variantStyles[currentAlert.variant];

  return (
    <Portal>
      <Animated.View
        style={[styles.wrapper, { opacity, paddingTop: Math.max(insets.top, spacing[4]) }]}>
        <View
          style={[
            styles.container,
            {
              backgroundColor: colors.backgroundColor,
              shadowColor: colors.shadowColor,
            },
          ]}>
          <View style={styles.textContainer}>
            <Text style={styles.text}>
              {t(currentAlert.text, { defaultValue: currentAlert.text })}
            </Text>
          </View>
          <Pressable onPress={dismiss} hitSlop={8} testID="snackbar-dismiss">
            <MaterialCommunityIcons name="close" size={iconSize.sm} color={unisonColors.white} />
          </Pressable>
        </View>
      </Animated.View>
    </Portal>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    paddingHorizontal: spacing[8],
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing[4],
    paddingVertical: spacing[2],
    borderRadius: radii.sm,
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.3,
    shadowRadius: 13,
    elevation: 6,
  },
  textContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    color: unisonColors.white,
    fontSize: fontSize.md,
    fontWeight: '500',
    lineHeight: 24,
    letterSpacing: 0.15,
    textAlign: 'center',
  },
});
